import { IonContent, IonFooter } from '@ionic/react';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import useAuth from '../../../../hooks/global/useAuth';
import useIsCheckoutDisabled from '../../../../hooks/basket/useIsCheckoutDisabled';
import PageHeader from '../../components/headers/PageHeader';
import useRouter from '../../../../hooks/navigation/useRouter';
import { Modals, Routes } from '../../exports/Enums';
import useModalRouter from '../../common/hooks/useModalRouter';
import useRestaurantClosedFallback from '../../common/hooks/useRestaurantClosedFallback';
import { useTranslation } from 'react-i18next';
import Button from '../../components/buttons/Button';
import CheckoutButton from '../../../../components-library/buttons/CheckoutButton';
import OrderValidationManager from '../../../../services/api/OrderValidationManager';
import useOrderFlow from '../../../../hooks/order/useOrderFlow';
import useDeliveryOrderMethodValidation from '../../modals/order-method/useDeliveryOrderMethodValidation';
import { useRef, useState } from 'react';
import Alert from '../../components/modals/Alert';
import { RenderableErrorResponse } from '../../../../services/exports/Types';
import useOrderErrorHandler from '../../common/hooks/error-handlers/useOrderErrorHandler';
import { swipeableCssClass } from '../../exports/Constants';
import OrderBasket from '../../../../components-library/order-basket';

const CartPage = (): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Pages:CartPage' });
  const { t: tErrors } = useTranslation(undefined, { keyPrefix: 'Modules:Mobile:Shared:Errors' });
  const { openModal, closeModal } = useModalRouter();
  const { isLoggedIn } = useAuth();
  const isCheckoutDisabled = useIsCheckoutDisabled();
  const { navigate, goBack } = useRouter();
  const { getOrderData } = useOrderFlow();
  const orderData = getOrderData();
  const { validate } = useRestaurantClosedFallback({
    orderMethod: orderData.method,
    scheduledFor: orderData.scheduled_for,
  });
  const orderErrorHandler = useOrderErrorHandler();
  const { handleValidationError } = useDeliveryOrderMethodValidation();
  const [isMobileAlertOpen, setIsMobileAlertOpen] = useState(false);
  const mobileAppGenericError = useRef(null);
  const toggleMobileAlert = () => setIsMobileAlertOpen((prev) => !prev);
  const [isLoading, setIsLoading] = useState(false);
  const onCheckoutClick = async (): Promise<void> => {
    setIsLoading(true);

    const isBusinessTimeValid = validate();

    if (!isBusinessTimeValid) {
      setIsLoading(false);
      return;
    }

    const { success, response } = await OrderValidationManager.validate(orderData);

    if (success) {
      setIsLoading(false);

      return navigate({
        mobile: {
          path: Routes.Checkout,
        },
      });
    }

    const errorResponse = response as any as RenderableErrorResponse;
    const validationError = handleValidationError(errorResponse, tErrors('internal_error'));

    setIsLoading(false);

    if (validationError) {
      return openModal({
        id: Modals.OrderMethod,
        props: {
          deliveryAddressError: validationError.deliveryAddress,
          scheduledForError: validationError.scheduledFor,
        },
      });
    }

    orderErrorHandler(errorResponse);

    mobileAppGenericError.current =
      errorResponse.response?.data?.description || errorResponse.response?.data?.title || tErrors('internal_error');
    toggleMobileAlert();
  };
  const onBasketItemUpdateClick = (index: number, basketItem: CompleteBasketItem): void => {
    openModal({
      id: Modals.MenuItem,
      presentation: {
        initialBreakpoint: 1,
        breakpoints: [0, 1],
        handle: false,
        cssClass: swipeableCssClass,
      },
      props: {
        product: basketItem.entry,
        isFreeItem: basketItem.is_free,
        lineItem: basketItem,
        onCloseModal: closeModal,
        basketItemIndex: index,
      },
    });
  };

  return (
    <>
      <Alert
        title={mobileAppGenericError.current}
        isOpen={isMobileAlertOpen}
        onDidDismiss={toggleMobileAlert}
        buttons={[
          {
            text: t('go_to_menu'),
            handler: goBack,
          },
        ]}
      />
      <PageHeader title={t('title')} />
      <IonContent>
        <OrderBasket
          onBasketItemUpdateClick={onBasketItemUpdateClick}
          onCheckoutClick={onCheckoutClick}
          loading={isLoading}
          checkoutDisabled={!isLoggedIn}
        />
      </IonContent>
      <IonFooter>
        <div className="!p-0 [&::part(container)]:p-0 safe-area-bottom">
          {isLoggedIn ? (
            <CheckoutButton
              loading={isLoading}
              title={t('buttons.checkout')}
              onClick={onCheckoutClick}
              hideAddProductsButton
              hideCashback
              disabled={isCheckoutDisabled}
            />
          ) : (
            <CheckoutButton
              title={t('buttons.checkout')}
              onClick={onCheckoutClick}
              hideAddProductsButton
              hideLoginPrompt
              hideCashback
              disabled={isCheckoutDisabled}
            >
              <Button title={t('buttons.login_to_checkout')} onClick={() => openModal({ id: Modals.Login })} />
            </CheckoutButton>
          )}
        </div>
      </IonFooter>
    </>
  );
};

export default CartPage;
